import React, { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection.jsx";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { encodeUrlParam } from "../../utils/urlUtils";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const STAFF_SORT_FIELDS = {
  name: "UserName",
  email: "email",
  // tbl_CompUser_Profile column is UserRole (maps to ComRolePermission_ID in the API model)
  userRole: "UserRole",
  city: "City",
  phone: "PhoneNo",
  status: "Active",
};

const STAFF_SORT_LABELS = {
  [STAFF_SORT_FIELDS.name]: "Name",
  [STAFF_SORT_FIELDS.email]: "Email",
  [STAFF_SORT_FIELDS.userRole]: "User Role",
  [STAFF_SORT_FIELDS.city]: "City",
  [STAFF_SORT_FIELDS.phone]: "Phone Number",
  [STAFF_SORT_FIELDS.status]: "Status",
};

function ManageStaff() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [roles, setRoles] = useState([]);
  const lastSelectedIndex = useRef(null);
  const staffsFetchSeqRef = useRef(0);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const pageKey = "staff";
  const defaultVisibleColumns = {
    email: true,
    phone: true,
    city: true,
    userRole: true,
    status: true,
  };
  const defaultSortOrder = { value: "DESC", label: "Desc" };
  const defaultSortBy = { value: "UserName", label: "User Name" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: STAFF_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    } else {
      // Migrate invalid/legacy sort keys (e.g. ComRolePermission_ID → UserRole)
      const allowedSortValues = new Set(Object.values(STAFF_SORT_FIELDS));
      if (!allowedSortValues.has(preferences.sortBy?.value)) {
        const migrated =
          preferences.sortBy?.value === "ComRolePermission_ID"
            ? {
                value: STAFF_SORT_FIELDS.userRole,
                label: STAFF_SORT_LABELS[STAFF_SORT_FIELDS.userRole],
              }
            : defaultSortBy;
        dispatch(setSortBy({ pageKey, sortBy: migrated }));
      }
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const FetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });
      const roleData = response.data.data || response.data;
      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
    } catch (err) {
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  useEffect(() => {
    FetchRoles();
  }, [FetchRoles]);

  const FetchStaffs = useCallback(async () => {
    const fetchSeq = ++staffsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
        },
      });

      if (fetchSeq !== staffsFetchSeqRef.current) return;

      const staffData = response.data.data || response.data;
      const records = response.data.totalRecords || 0;

      if (Array.isArray(staffData)) {
        const validStaffs = staffData.filter((staff) => staff != null);
        setStaffs(validStaffs);
        setTotalRecords(records);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setStaffs([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      // Only refresh catalog emptiness from successful unfiltered loads.
      // Avoid treating a bad sort/API empty payload as "no staff exist".
      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== staffsFetchSeqRef.current) return;
      // 401/session expiry is handled by axios (refresh → force logout → /user/login).
      if (err?.response?.status !== 401) {
        showErrorToast("Failed to fetch staff.");
      }
    } finally {
      if (fetchSeq === staffsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [
    search,
    pageSize,
    sortBy,
    sortOrder,
    pageNumber,
    CompID,
    dispatch,
    navigate,
  ]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(staffsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => FetchStaffs(), [FetchStaffs]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageNumber, pageSize?.value, sortBy?.value, sortOrder?.value]);

  const DeleteStaff = async (staff) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${staff.userName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_COMPANY_STAFF, {
          params: {
            CompID: staff.compID,
            UserName: staff.userName,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setStaffs((prevStaffs) =>
              prevStaffs.filter((delStaff) => delStaff.userName !== staff.userName)
            );
            showSuccessToast("Staff is Deleted.");
          } else {
            showErrorToast(response.data.message);
          }
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (userName, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (staffs[i]?.userName) {
            newSelected.add(staffs[i].userName);
          }
        }
      } else {
        if (newSelected.has(userName)) {
          newSelected.delete(userName);
        } else {
          newSelected.add(userName);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(staffs.map((item) => item.userName));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No staff selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected staff members?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((userName) =>
          axiosInstance.delete(APIs.DELETE_COMPANY_STAFF, {
            params: {
              CompID,
              UserName: userName,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      FetchStaffs();
      showSuccessToast(
        `${selectedItems.size} staff members deleted successfully.`
      );
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected staff.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No staff selected for editing.");
      return;
    }

    const selectedStaffUserNames = Array.from(selectedItems);
    navigate(`/staff/bulkEdit`, { state: { selectedStaffUserNames } });
  };

  const handleBulkStatusChange = async (activate) => {
    if (selectedItems.size === 0) {
      showErrorToast(
        `No staff selected to ${activate ? "activate" : "deactivate"}.`
      );
      return;
    }

    try {
      const confirmAction = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${
          activate ? "activate" : "deactivate"
        } ${selectedItems.size} selected staff members?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmAction) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((userName) =>
          axiosInstance.put(APIs.UPDATE_STAFF_STATUS, {
            CompID,
            UserName: userName,
            Active: activate,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      FetchStaffs();
      showSuccessToast(
        `${selectedItems.size} staff members ${
          activate ? "activated" : "deactivated"
        } successfully.`
      );
      setLoading(false);
    } catch (err) {
      showErrorToast(
        `Failed to ${activate ? "activate" : "deactivate"} selected staff.`
      );
      setLoading(false);
    }
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No staff match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no staff members to show right now. Add a new staff member or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || staffs.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No staff found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Staff</li>
          </ol>
          <h1 className="page-header mb-0">Manage Staff</h1>
        </div>
        <div className="ms-auto">
          <Link to="/staff/addStaff">
            <ThemeButton icon="fa fa-plus">Add Staff</ThemeButton>
          </Link>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={() => navigate("/staff/addStaff")} title="Add Staff" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Staff"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, email, phone..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} staff member
                        {selectedItems.size > 1 ? "s" : ""} selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Staff"
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Staff"
                      >
                        <i className="fa fa-trash me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {staffs.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  onChange={handleSelectAll}
                                  checked={
                                    staffs.length > 0 &&
                                    selectedItems.size === staffs.length
                                  }
                                />
                                <label className="form-check-label" />
                              </div>
                            </th>
                          )}
                          <SortableColumnHeader
                            sortField={STAFF_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.email && (
                            <SortableColumnHeader
                              sortField={STAFF_SORT_FIELDS.email}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Email
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.userRole && (
                            <SortableColumnHeader
                              sortField={STAFF_SORT_FIELDS.userRole}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              User Role
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.city && (
                            <SortableColumnHeader
                              sortField={STAFF_SORT_FIELDS.city}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              City
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.phone && (
                            <SortableColumnHeader
                              sortField={STAFF_SORT_FIELDS.phone}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Phone Number
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.status && (
                            <SortableColumnHeader
                              sortField={STAFF_SORT_FIELDS.status}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Status
                            </SortableColumnHeader>
                          )}
                          <th className="pt-0 pb-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffs.map((staff, index) => (
                          <tr key={staff.userName}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="w-10px align-middle">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedItems.has(staff.userName)}
                                    onChange={(e) =>
                                      handleSelectItem(staff.userName, index, e)
                                    }
                                    data-chkbox-shiftsel="type1"
                                  />
                                  <label className="form-check-label" />
                                </div>
                              </td>
                            )}
                            <td
                              className="align-middle"
                              onClick={() => {
                                navigate(
                                  `/staff/updateStaff/${encodeUrlParam(staff.userName)}`
                                );
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {staff.firstName} {staff.lastName}
                            </td>
                            {visibleColumns.email && (
                              <td
                                className="align-middle"
                                onClick={() => {
                                  navigate(
                                    `/staff/updateStaff/${encodeUrlParam(staff.userName)}`
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {staff.email}
                              </td>
                            )}
                            {visibleColumns.userRole && (
                              <td
                                className="align-middle"
                                onClick={() => {
                                  navigate(
                                    `/staff/updateStaff/${encodeUrlParam(staff.userName)}`
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {roles.find((r) => r.value === staff.comRolePermission_ID)
                                  ?.label || "N/A"}
                              </td>
                            )}
                            {visibleColumns.city && (
                              <td
                                className="align-middle"
                                onClick={() => {
                                  navigate(
                                    `/staff/updateStaff/${encodeUrlParam(staff.userName)}`
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {staff.city || "N/A"}
                              </td>
                            )}
                            {visibleColumns.phone && (
                              <td
                                className="align-middle"
                                onClick={() => {
                                  navigate(
                                    `/staff/updateStaff/${encodeUrlParam(staff.userName)}`
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {staff.phoneNo}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle">
                                {staff.active ? (
                                  <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                    <i className="fa fa-circle fs-9px fa-fw me-5px" /> Active
                                  </span>
                                ) : (
                                  <span className="badge border border-warning text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                                    <i className="fa fa-circle fs-9px fa-fw me-5px" /> Pending
                                  </span>
                                )}
                              </td>
                            )}
                            <td>
                              <Link
                                to={`/staff/updateStaff/${encodeUrlParam(staff.userName)}`}
                                className="ms-3"
                              >
                                <span className="fas fa-pencil" />
                              </Link>
                              <span
                                onClick={() => DeleteStaff(staff)}
                                className="fas fa-trash ms-3"
                                style={{ cursor: "pointer", color: "red" }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
}

export default ManageStaff;
